import { DatesProvider } from '@mantine/dates';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import type { LoginResponse } from '../hooks/useHfwLogin';
import OauthServiceMS from '../services/OauthServiceMS';
import ProfileService from '../services/ProfileService';
import type { UserResponse } from '../types/api/response/user';
import { Language } from '../types/i18n';
import {
  getPersistedObject,
  persistUser,
  removePersistedObject,
} from '../utils/localStorage';
import { APP_NAME } from '../variables/GlobalVariables';

type AuthContextValue = {
  user: UserResponse;
  updateUser: (
    user: UserResponse | null,
    access_token: string | null,
    refresh_token: string | null,
    redirectUri: string | null
  ) => void;
  logout: () => void;
  access_token: string;
  refresh_token: string;
  loginResponse: any;
  appInitialized: boolean;
  isWhiteLabelGma: boolean;
  redirectWithAccessTokenParam: (
    _redirectUri: string | null,
    _access_token: string,
    _refresh_token: string
  ) => void;
};

export const AuthContext = createContext<AuthContextValue>(
  {} as AuthContextValue
);
const useAuthContext = () => useContext(AuthContext);
export const useAuth = useAuthContext;

type AuthProviderProps = {
  children: ReactNode;
};

export default function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<UserResponse>(getPersistedObject('user'));
  const [accessToken, setAccessToken] = useState<any>(
    getPersistedObject('access_token')
  );
  const [refreshToken, setRefreshToken] = useState<any>(
    getPersistedObject('refresh_token')
  );
  const [loginResponse, setLoginResponse] = useState<LoginResponse | undefined>(
    undefined
  );
  const [redirectUri, setRedirectUri] = useState<string | null>(null);
  const navigate = useNavigate();
  const userLang = useMemo(() => {
    if (user?.lang) return user.lang;

    return Language.fr;
  }, [user]);

  const isWhiteLabelGma: boolean = useMemo(() => {
    return (
      APP_NAME.toLowerCase().includes('gma') ||
      APP_NAME.toLowerCase().includes('gerermesaffaires')
    );
  }, [APP_NAME]);

  useEffect(() => {
    dayjs.locale(userLang);
  }, [userLang]);

  useEffect(() => {
    if (redirectUri && accessToken && refreshToken) {
      try {
        const url = new URL(redirectUri);
        navigate(url.pathname);
      } catch (_ignored) {}
    }
    return () => {
      setRedirectUri(null);
    };
  }, [redirectUri, accessToken, refreshToken]);

  useQuery({
    enabled: !!accessToken,
    queryKey: ['ProfileService.getUserHfwProfiles', accessToken],
    queryFn: () => ProfileService.getUserHfwProfiles(accessToken),
    onSuccess: (data) => {
      // AxiosClient a pu rafraîchir les jetons pendant la requête : il ne les écrit que
      // dans le localStorage, le state porte encore les anciens.
      const persistedAccessToken = getPersistedObject('access_token');
      const persistedRefreshToken = getPersistedObject('refresh_token');
      setLoginResponse({
        access_token: persistedAccessToken,
        refresh_token: persistedRefreshToken,
        users: data,
      });
      if (data?.length && user?.id) {
        const _user = data.find((item) => item.id === user.id);
        if (_user) {
          updateUser(_user, persistedAccessToken, persistedRefreshToken, null);
        }
      }
    },
  });
  const { refetch: refetchLogout } = useQuery({
    enabled: false,
    queryKey: ['OauthServiceMS.logout'],
    queryFn: OauthServiceMS.logout,
  });

  function updateUser(
    _user: UserResponse | null,
    access_token: string | null,
    refresh_token: string | null,
    redirectUri: string | null
  ) {
    if (_user) {
      setUser(_user);
    }
    setAccessToken(access_token);
    setRefreshToken(refresh_token);
    if (access_token && refresh_token) {
      persistUser(_user, access_token, refresh_token);
    }
    setRedirectUri(redirectUri);
  }

  function logout() {
    clearStorageAndRedirect();
    removePersistedObject('gma');
  }

  function clearStorageAndRedirect() {
    refetchLogout();
    removePersistedObject('user');
    removePersistedObject('access_token');
    removePersistedObject('refresh_token');
  }

  function redirectWithAccessTokenParam(
    _redirectUri: string | null,
    _access_token: string,
    _refresh_token: string
  ) {
    if (_redirectUri !== null) {
      try {
        const url = new URL(_redirectUri);
        navigate(url.pathname, {
          state: { accessToken: _access_token, refreshToken: _refresh_token },
        });
      } catch (_ignored) {}
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        updateUser,
        logout,
        access_token: accessToken,
        refresh_token: refreshToken,
        loginResponse,
        appInitialized: Boolean(user && accessToken && refreshToken),
        isWhiteLabelGma,
        redirectWithAccessTokenParam,
      }}
    >
      <DatesProvider settings={{ locale: userLang }}>{children}</DatesProvider>
    </AuthContext.Provider>
  );
}
